import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { forkJoin } from 'rxjs';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Interfaces
import IPatient from '../../../../_interfaces/IPatients';
import IPatientHasTreatment from '../../../../_interfaces/IPatientHasTreatment';
import ITreatment from '../../../../_interfaces/ITreatment';
import ISite from '../../../../_interfaces/ISite';
import ICurrency from '../../../../_interfaces/ICurrency';
import IUser from '../../../../_interfaces/IUser';
import ICalendar from '../../../../_interfaces/ICalendar';

// Services
import { PatientsService } from '../../../../_services/patients/patients.service';
import { TreatmentsService } from '../../../../_services/treatments/treatments.service';
import { SessionService } from '../../../../_services/session/session.service';
import { SitesService } from '../../../../_services/sites/sites.service';
import { PaymentsService } from '../../../../_services/payments/payments.service';
import { TreatmentUtilsService } from '../../../../_services/treatments/treatment-utils.service';
import { CurrenciesService } from '../../../../_services/currencies/currencies.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

// Shared Components
import { TreatmentItemComponent } from '../../../shared/treatment-item/treatment-item.component';
import {
  TreatmentAddFormComponent,
  NewTreatmentFormData,
} from '../../../shared/treatment-add-form/treatment-add-form.component';

/**
 * Estado interno de un tratamiento en el manager
 */
interface TreatmentItem {
  patientTreatment: IPatientHasTreatment;
  selected: boolean;
  isPaid: boolean;
  remainingSessions?: number;
  deleting?: boolean;
  removingPayment?: boolean;
}

/**
 * TreatmentManagerComponent
 *
 * Gestiona la selección y creación de tratamientos durante la creación de una cita.
 * Permite:
 * 1. Seleccionar tratamientos existentes del paciente (disponibles)
 * 2. Añadir nuevos tratamientos del catálogo al paciente y seleccionarlos
 *
 * @example
 * <app-treatment-manager
 *   [patient]="patient"
 *   [site]="site"
 *   [customersId]="customers_id"
 *   (selectedTreatmentsChange)="onTreatmentsSelected($event)"
 * />
 */
@Component({
  standalone: true,
  selector: 'app-treatment-manager',
  templateUrl: './treatment-manager.component.html',
  styleUrls: ['./treatment-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ButtonModule,
    MessageModule,
    ProgressSpinnerModule,
    SelectModule,
    TreatmentItemComponent,
    TreatmentAddFormComponent,
  ],
})
export class TreatmentManagerComponent implements OnInit, OnChanges {
  private static readonly PROFESSIONAL_ASSIGNMENT_MODES = {
    SINGLE: 'single',
    PER_TREATMENT: 'per_treatment',
  } as const;
  // =========================================================================
  // INPUTS
  // =========================================================================

  /** Paciente seleccionado */
  @Input() patient: IPatient | null = null;

  /** Site actual */
  @Input() site: ISite | null = null;

  /** Calendario seleccionado */
  @Input() calendar: ICalendar | null = null;

  /** ID del customer */
  @Input() customersId: number | null = null;

  /** Mostrar panel de asignación de profesionales */
  @Input() showProfessionalPanel = true;

  /** Permitir acciones de pago en cada tratamiento */
  @Input() allowPaymentActions = true;

  /** Estilo simple embebido (sin cajas pesadas) */
  @Input() simpleLayout = false;

  // =========================================================================
  // OUTPUTS
  // =========================================================================

  /** Emite cuando cambian los tratamientos seleccionados */
  @Output() selectedTreatmentsChange = new EventEmitter<
    IPatientHasTreatment[]
  >();

  // =========================================================================
  // VIEW CHILDREN
  // =========================================================================

  @ViewChild(TreatmentAddFormComponent)
  addFormComponent: TreatmentAddFormComponent;

  // =========================================================================
  // STATE
  // =========================================================================

  /** Lista de tratamientos del paciente con estado */
  treatmentItems: TreatmentItem[] = [];

  /** Tratamientos seleccionados para la cita */
  selectedTreatments: IPatientHasTreatment[] = [];

  /** Estado de carga */
  loading = false;

  /** Mostrar/ocultar formulario de nuevo tratamiento */
  showAddForm = false;

  /** Estado de guardado del nuevo tratamiento */
  savingNewTreatment = false;

  /** Mensaje de error */
  errorMessage: string | null = null;

  /** Mensaje de éxito */
  successMessage: string | null = null;

  /** Moneda activa (cargada del site) - igual que en form-create-patient-treatment */
  currency: ICurrency | null = null;

  /** Profesionales elegibles en la clínica */
  professionals: IUser[] = [];

  /** Profesionales admitidos por tratamiento de catálogo */
  allowedProfessionalIdsByTreatmentId: Record<number, number[]> = {};

  /** Profesional elegido por cada tratamiento del paciente */
  selectedProfessionalByPatientTreatmentId: Record<number, number | null> = {};

  /** Modo de asignación */
  professionalAssignmentMode: 'single' | 'per_treatment' =
    TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES.PER_TREATMENT;

  /** Profesional global para todos los tratamientos seleccionados */
  globalProfessionalId: number | null = null;

  // =========================================================================
  // CONSTRUCTOR
  // =========================================================================

  constructor(
    private patientsService: PatientsService,
    private treatmentsService: TreatmentsService,
    private sessionService: SessionService,
    private sitesService: SitesService,
    private currenciesService: CurrenciesService,
    private paymentsService: PaymentsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    public treatmentUtils: TreatmentUtilsService,
  ) {}

  get canManagePayments(): boolean {
    return this.sessionService.canManagePayments(
      this.resolveCustomer(),
      this.site || this.sessionService.getSite(),
    );
  }

  get canManagePatientTreatments(): boolean {
    return this.hasPermissionOnCurrentSite('PATIENTS.UPDATE');
  }

  private getEffectiveSiteId(): number | undefined {
    const siteId = Number(this.site?.id || 0);
    return Number.isFinite(siteId) && siteId > 0 ? siteId : undefined;
  }

  // =========================================================================
  // LIFECYCLE
  // =========================================================================

  ngOnInit(): void {
    // Cargar el site con currencies - igual que en form-create-patient-treatment
    this.loadSiteCurrencies();
    this.loadProfessionalContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando cambia el paciente, recargar sus tratamientos
    if (changes['patient'] && this.patient?.id) {
      this.loadPatientTreatments();
    }

    // Si cambia el paciente a null, limpiar todo
    if (changes['patient'] && !this.patient?.id) {
      this.clearAll();
    }

    // Si cambia el site, recargar currencies
    if (changes['site'] && this.site?.id) {
      this.loadSiteCurrencies();
      this.loadProfessionalContext();
      if (this.patient?.id) {
        this.loadPatientTreatments();
      }
    }

    if (changes['calendar']) {
      this.selectedProfessionalByPatientTreatmentId = {};
      if (
        this.professionalAssignmentMode ===
          TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES.SINGLE &&
        this.globalProfessionalId === null
      ) {
        this.globalProfessionalId = this.getCalendarDefaultProfessionalId();
      }
      this.hydrateProfessionalDefaults();
      this.updateSelectedTreatments();
    }
  }

  get assignmentModeOptions(): Array<{ label: string; value: 'single' | 'per_treatment' }> {
    return [
      {
        label: this.translateService.instant(
          'TREATMENT_MANAGER.PROFESSIONAL.MODE.PER_TREATMENT',
        ),
        value: TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES
          .PER_TREATMENT,
      },
      {
        label: this.translateService.instant(
          'TREATMENT_MANAGER.PROFESSIONAL.MODE.SINGLE',
        ),
        value: TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES.SINGLE,
      },
    ];
  }

  /**
   * Carga la moneda activa del site
   */
  private loadSiteCurrencies(): void {
    if (!this.customersId || !this.site?.id) return;

    this.sitesService.get(this.customersId, this.site.id).subscribe({
      next: (site) => {
        this.currenciesService.getAll().subscribe({
          next: (currencies) => {
            const siteCurrencyCode = String(site?.currency || '').toUpperCase();
            this.currency =
              currencies.find(
                (currency) => String(currency.code || '').toUpperCase() === siteCurrencyCode,
              ) || null;
            this.cdr.markForCheck();
          },
          error: () => {
            this.currency = null;
            this.cdr.markForCheck();
          },
        });
      },
      error: () => {
        this.currency = null;
        this.cdr.markForCheck();
      },
    });
  }

  private loadProfessionalContext(): void {
    if (!this.customersId || !this.site?.id) return;

    forkJoin({
      users: this.sitesService.getUsers(this.customersId, this.site.id),
      treatments: this.treatmentsService.getFromSite(this.customersId, {
        id: this.site.id,
      } as ISite),
    }).subscribe({
      next: ({ users, treatments }) => {
        this.professionals = (users || []).map((user) => ({
          ...user,
          fullName: user.surname ? `${user.name} ${user.surname}` : user.name,
        })) as any;

        this.allowedProfessionalIdsByTreatmentId = {};
        (treatments || []).forEach((treatment) => {
          if (!treatment?.id) return;
          this.allowedProfessionalIdsByTreatmentId[treatment.id] = (
            treatment.allowed_users_ids || []
          )
            .map((value: any) => Number(value))
            .filter((value: number) => Number.isInteger(value) && value > 0);
        });
        this.hydrateProfessionalDefaults();
        this.updateSelectedTreatments();
        this.cdr.markForCheck();
      },
      error: () => {
        this.professionals = [];
        this.allowedProfessionalIdsByTreatmentId = {};
        this.cdr.markForCheck();
      },
    });
  }

  private getAllowedProfessionalsForTreatmentId(treatmentId: number): IUser[] {
    const allowedIds = this.allowedProfessionalIdsByTreatmentId[treatmentId] || [];
    if (!allowedIds.length) {
      return this.professionals;
    }

    return this.professionals.filter((professional) =>
      allowedIds.includes(Number(professional.id || 0)),
    );
  }

  private getAutoSelectedProfessionalForPatientTreatment(
    treatment: IPatientHasTreatment,
  ): number | null {
    const treatmentId = Number(treatment?.treatments_id || 0);
    const allowedProfessionals =
      this.getAllowedProfessionalsForTreatmentId(treatmentId);
    return allowedProfessionals.length === 1
      ? Number(allowedProfessionals[0].id || 0)
      : null;
  }

  private getCalendarDefaultProfessionalId(): number | null {
    const calendarUsersId = Number(this.calendar?.users_id || 0);
    return Number.isInteger(calendarUsersId) && calendarUsersId > 0
      ? calendarUsersId
      : null;
  }

  private hydrateProfessionalDefaults(): void {
    this.treatmentItems.forEach((item) => {
      const treatmentId = Number(item?.patientTreatment?.id || 0);
      if (!Number.isInteger(treatmentId) || treatmentId <= 0) return;
      if (Object.prototype.hasOwnProperty.call(this.selectedProfessionalByPatientTreatmentId, treatmentId)) {
        return;
      }
      this.selectedProfessionalByPatientTreatmentId[treatmentId] =
        this.getAutoSelectedProfessionalForPatientTreatment(item.patientTreatment);
    });
  }

  getProfessionalOptionsForItem(item: TreatmentItem): IUser[] {
    return this.getAllowedProfessionalsForTreatmentId(
      Number(item?.patientTreatment?.treatments_id || 0),
    );
  }

  getGlobalProfessionalOptions(): IUser[] {
    const selectedItems = this.treatmentItems.filter((item) => item.selected);
    if (!selectedItems.length) {
      return this.professionals;
    }

    let restrictedIds: number[] | null = null;
    selectedItems.forEach((item) => {
      const treatmentId = Number(item?.patientTreatment?.treatments_id || 0);
      const allowedIds = this.allowedProfessionalIdsByTreatmentId[treatmentId] || [];
      if (!allowedIds.length) {
        return;
      }

      restrictedIds =
        restrictedIds === null
          ? [...allowedIds]
          : restrictedIds.filter((id) => allowedIds.includes(id));
    });

    if (restrictedIds === null) {
      return this.professionals;
    }

    return this.professionals.filter((professional) =>
      restrictedIds!.includes(Number(professional.id || 0)),
    );
  }

  private normalizeGlobalProfessionalSelection(): void {
    const optionIds = new Set(
      this.getGlobalProfessionalOptions().map((professional) =>
        Number(professional.id || 0),
      ),
    );

    if (
      this.globalProfessionalId !== null &&
      !optionIds.has(Number(this.globalProfessionalId || 0))
    ) {
      this.globalProfessionalId = null;
    }
  }

  // =========================================================================
  // DATA LOADING
  // =========================================================================

  /**
   * Carga los tratamientos del paciente
   */
  loadPatientTreatments(): void {
    if (!this.patient?.id || !this.customersId) return;

    this.loading = true;
    this.errorMessage = null;

    this.patientsService
      .getTreatments(
        this.customersId,
        this.patient.id,
        this.getEffectiveSiteId(),
      )
      .subscribe({
        next: (treatments: IPatientHasTreatment[]) => {
          // Filtrar solo tratamientos disponibles (no asignados a citas)
          const availableTreatments = treatments.filter((t) => !t.appointment);

          // Mapear a items con estado
          this.treatmentItems = availableTreatments.map((t) => ({
            patientTreatment: t,
            selected: false,
            isPaid: this.isTreatmentPaid(t),
            remainingSessions: this.calculateRemainingSessions(t),
          }));
          this.hydrateProfessionalDefaults();

          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading patient treatments:', error);
          this.errorMessage = this.translateService.instant(
            'ERRORS.LOADING_TREATMENTS',
          );
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Carga los tratamientos del paciente y selecciona automáticamente los nuevos
   * (los que tienen el treatments_id especificado)
   */
  loadPatientTreatmentsAndSelectNew(newTreatmentId: number): void {
    if (!this.patient?.id || !this.customersId) return;

    // Guardar los IDs de tratamientos ya existentes antes de recargar
    const existingIds = new Set(
      this.treatmentItems.map((t) => t.patientTreatment.id),
    );

    this.patientsService
      .getTreatments(
        this.customersId,
        this.patient.id,
        this.getEffectiveSiteId(),
      )
      .subscribe({
        next: (treatments: IPatientHasTreatment[]) => {
          // Filtrar solo tratamientos disponibles (no asignados a citas)
          const availableTreatments = treatments.filter((t) => !t.appointment);

          // Mapear a items con estado, seleccionando los nuevos
          this.treatmentItems = availableTreatments.map((t) => ({
            patientTreatment: t,
            // Seleccionar si es nuevo (no existía antes) Y es del tipo de tratamiento que acabamos de añadir
            selected:
              !existingIds.has(t.id) && t.treatments_id === newTreatmentId,
            isPaid: this.isTreatmentPaid(t),
            remainingSessions: this.calculateRemainingSessions(t),
          }));
          this.hydrateProfessionalDefaults();

          // Actualizar la selección
          this.updateSelectedTreatments();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error reloading patient treatments:', error);
          this.cdr.markForCheck();
        },
      });
  }

  // =========================================================================
  // TREATMENT SELECTION
  // =========================================================================

  /**
   * Toggle selección de un tratamiento
   */
  toggleTreatment(item: TreatmentItem): void {
    item.selected = !item.selected;
    if (
      item.selected &&
      this.professionalAssignmentMode ===
        TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES.PER_TREATMENT
    ) {
      const patientTreatmentId = Number(item.patientTreatment?.id || 0);
      if (
        patientTreatmentId > 0 &&
        !Object.prototype.hasOwnProperty.call(
          this.selectedProfessionalByPatientTreatmentId,
          patientTreatmentId,
        )
      ) {
        this.selectedProfessionalByPatientTreatmentId[patientTreatmentId] =
          this.getAutoSelectedProfessionalForPatientTreatment(item.patientTreatment);
      }
    }
    this.normalizeGlobalProfessionalSelection();
    this.updateSelectedTreatments();
  }

  /**
   * Seleccionar/deseleccionar todos los tratamientos
   */
  toggleSelectAll(): void {
    const allSelected = this.areAllSelected();
    this.treatmentItems.forEach((item) => {
      item.selected = !allSelected;
    });
    this.normalizeGlobalProfessionalSelection();
    this.updateSelectedTreatments();
  }

  /**
   * Verificar si todos están seleccionados
   */
  areAllSelected(): boolean {
    return (
      this.treatmentItems.length > 0 &&
      this.treatmentItems.every((item) => item.selected)
    );
  }

  /**
   * Actualiza la lista de tratamientos seleccionados y emite el evento
   */
  private updateSelectedTreatments(): void {
    this.selectedTreatments = this.treatmentItems
      .filter((item) => item.selected)
      .map((item) => {
        const users_id =
          this.professionalAssignmentMode ===
          TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES.SINGLE
            ? this.globalProfessionalId ?? null
            : this.getTreatmentProfessionalId(item);
        return {
          ...item.patientTreatment,
          users_id,
        } as IPatientHasTreatment;
      });

    this.selectedTreatmentsChange.emit(this.selectedTreatments);
    this.cdr.markForCheck();
  }

  onAssignmentModeChange(mode: 'single' | 'per_treatment'): void {
    this.professionalAssignmentMode = mode;
    if (
      mode === TreatmentManagerComponent.PROFESSIONAL_ASSIGNMENT_MODES.SINGLE &&
      this.globalProfessionalId === null
    ) {
      this.globalProfessionalId = this.getCalendarDefaultProfessionalId();
    }
    this.normalizeGlobalProfessionalSelection();
    this.updateSelectedTreatments();
  }

  onGlobalProfessionalChanged(users_id: number | null): void {
    this.globalProfessionalId = users_id ?? null;
    this.updateSelectedTreatments();
  }

  onTreatmentProfessionalChange(
    item: TreatmentItem,
    users_id: number | null,
  ): void {
    const treatmentId = Number(item?.patientTreatment?.id || 0);
    if (!Number.isInteger(treatmentId) || treatmentId <= 0) return;
    this.selectedProfessionalByPatientTreatmentId[treatmentId] = users_id ?? null;
    this.normalizeGlobalProfessionalSelection();
    this.updateSelectedTreatments();
  }

  getTreatmentProfessionalId(item: TreatmentItem): number | null {
    const treatmentId = Number(item?.patientTreatment?.id || 0);
    if (!Number.isInteger(treatmentId) || treatmentId <= 0) return null;
    if (
      Object.prototype.hasOwnProperty.call(
        this.selectedProfessionalByPatientTreatmentId,
        treatmentId,
      )
    ) {
      return this.selectedProfessionalByPatientTreatmentId[treatmentId] ?? null;
    }
    return this.getAutoSelectedProfessionalForPatientTreatment(item.patientTreatment);
  }

  // =========================================================================
  // ADD NEW TREATMENT
  // =========================================================================

  /**
   * Toggle formulario de añadir tratamiento
   */
  toggleAddForm(): void {
    if (!this.canManagePatientTreatments) return;
    this.showAddForm = !this.showAddForm;
    if (!this.showAddForm && this.addFormComponent) {
      this.addFormComponent.reset();
    }
  }

  /**
   * Añadir nuevo tratamiento al paciente (callback del formulario compartido)
   */
  onAddFormSubmit(formData: NewTreatmentFormData): void {
    if (!formData.treatment || !this.patient?.id || !this.customersId) return;

    this.savingNewTreatment = true;
    this.errorMessage = null;
    this.successMessage = null;

    const { treatment, units, price } = formData;

    this.patientsService
      .addTreatment(this.customersId, this.patient, treatment, {
        units,
        price,
      })
      .subscribe({
        next: () => {
          this.successMessage = this.translateService.instant(
            'TREATMENT_MANAGER.ADDED_SUCCESS',
            { name: treatment.name },
          );

          // Recargar y seleccionar nuevos tratamientos
          this.loadPatientTreatmentsAndSelectNew(treatment.id!);

          // Resetear formulario
          if (this.addFormComponent) {
            this.addFormComponent.reset();
          }
          this.showAddForm = false;
          this.savingNewTreatment = false;

          setTimeout(() => {
            this.successMessage = null;
            this.cdr.markForCheck();
          }, 3000);

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error adding treatment:', error);
          this.errorMessage = this.translateService.instant(
            'ERRORS.ADDING_TREATMENT',
          );
          this.savingNewTreatment = false;
          this.cdr.markForCheck();
        },
      });
  }

  // =========================================================================
  // HELPERS
  // =========================================================================

  /**
   * Verificar si un tratamiento está pagado (delegado a TreatmentUtilsService)
   */
  private isTreatmentPaid(treatment: IPatientHasTreatment): boolean {
    return this.treatmentUtils.isTreatmentPaid(treatment);
  }

  /**
   * Calcular sesiones restantes (placeholder - ajustar según modelo real)
   */
  private calculateRemainingSessions(treatment: IPatientHasTreatment): number {
    // Por ahora retornamos 1, ajustar según el modelo de datos real
    return 1;
  }

  /**
   * Limpiar todo el estado
   */
  private clearAll(): void {
    this.treatmentItems = [];
    this.selectedTreatments = [];
    this.showAddForm = false;
    if (this.addFormComponent) {
      this.addFormComponent.reset();
    }
    this.errorMessage = null;
    this.successMessage = null;
    this.globalProfessionalId = null;
    this.selectedProfessionalByPatientTreatmentId = {};
    this.selectedTreatmentsChange.emit([]);
    this.cdr.markForCheck();
  }

  /**
   * Obtener el total de tratamientos seleccionados
   */
  get selectedCount(): number {
    return this.treatmentItems.filter((t) => t.selected).length;
  }

  /**
   * Verificar si hay tratamientos disponibles
   */
  get hasAvailableTreatments(): boolean {
    return this.treatmentItems.length > 0;
  }

  /**
   * Calcular el total de precio de tratamientos seleccionados
   */
  get selectedTotal(): number {
    return this.treatmentItems
      .filter((t) => t.selected)
      .reduce((sum, t) => sum + (t.patientTreatment.price || 0), 0);
  }

  /**
   * Formatear precio (delegado a TreatmentUtilsService)
   */
  formatPrice(price: number, currency?: ICurrency): string {
    return this.treatmentUtils.formatPrice(price, currency || null);
  }

  /**
   * Obtener clase de estado de pago (delegado a TreatmentUtilsService)
   */
  getPaymentStatusClass(item: TreatmentItem): string {
    return this.treatmentUtils.getPaymentStatusClass(item.isPaid);
  }

  /**
   * Obtener label de estado de pago (delegado a TreatmentUtilsService)
   */
  getPaymentStatusLabel(item: TreatmentItem): string {
    return this.treatmentUtils.getPaymentStatusLabel(item.isPaid);
  }

  /**
   * TrackBy para la lista de tratamientos
   */
  trackByTreatmentId(index: number, item: TreatmentItem): number {
    return item.patientTreatment.id;
  }

  // =========================================================================
  // PAYMENT METHODS
  // =========================================================================

  /**
   * Abrir diálogo de pago para un tratamiento (delegado a TreatmentUtilsService)
   */
  openPaymentDialog(item: TreatmentItem): void {
    if (!this.canManagePayments) return;
    const treatment = item.patientTreatment;
    if (!treatment) return;

    this.treatmentUtils.openPaymentDialog(treatment, () => {
      this.loadPatientTreatments();
    });
  }

  /**
   * Quitar pago de un tratamiento (delegado a TreatmentUtilsService)
   */
  removePayment(item: TreatmentItem): void {
    if (!this.canManagePayments) return;
    const treatment = item.patientTreatment;
    if (!treatment.payments?.length) return;

    item.removingPayment = true;
    this.cdr.markForCheck();

    this.treatmentUtils
      .removePayment(this.customersId!, treatment, () => {
        item.removingPayment = false;
        this.loadPatientTreatments();
      })
      .subscribe({
        error: () => {
          item.removingPayment = false;
          this.cdr.markForCheck();
        },
      });
  }

  // =========================================================================
  // DELETE METHODS
  // =========================================================================

  /**
   * Confirmar eliminación de un tratamiento (delegado a TreatmentUtilsService)
   */
  confirmDeleteTreatment(item: TreatmentItem): void {
    if (!this.canManagePatientTreatments) return;
    item.deleting = true;
    this.cdr.markForCheck();

    this.treatmentUtils
      .deleteTreatment(
        this.customersId!,
        this.patient!.id!,
        item.patientTreatment,
        () => {
          if (item.selected) {
            this.updateSelectedTreatments();
          }
          this.loadPatientTreatments();
        },
      )
      .subscribe({
        error: () => {
          item.deleting = false;
          this.cdr.markForCheck();
        },
      });
  }

  private canAccessModule(identifier: string, permissions: string[] = []): boolean {
    if (!this.hasCommercialModuleEnabled(identifier)) {
      return false;
    }

    if (!permissions.length) {
      return true;
    }

    return permissions.some((permission) =>
      this.hasPermissionOnCurrentSite(permission),
    );
  }

  private hasCommercialModuleEnabled(identifier: string): boolean {
    const normalized = `${identifier || ''}`.trim().toLowerCase();
    const moduleIdentifier =
      normalized === 'statistics' ? 'analytics' : normalized;

    if (moduleIdentifier === 'appointments' || moduleIdentifier === 'patients') {
      return true;
    }

    const customer = this.resolveCustomer();
    const modules = customer?.commercialModules as any;

    if (!modules) {
      return true;
    }

    return modules?.[moduleIdentifier] !== false;
  }

  private hasPermissionOnCurrentSite(resource: string): boolean {
    const customer = this.resolveCustomer();
    const site = this.site || this.sessionService.getSite();

    if (!customer || !site) {
      return false;
    }

    return this.sessionService.hasPermission(customer, site, resource);
  }

  private resolveCustomer() {
    const current = this.sessionService.getCustomer();
    if (current?.id && (!this.customersId || +current.id === +this.customersId)) {
      return current;
    }

    const permissions = this.sessionService.getPermissions() || [];
    return (
      permissions.find((customer) => +customer.id === +this.customersId) ||
      current
    );
  }
}
